/**
 * @fileoverview Edit Assignment Modal Component
 * @module components/modals/EditAssignmentModal
 * @description Modal for editing existing assignment details including title, description, doctor, and session time
 * UPDATED: 10-10-2025 20:49:23 - Removed status management (assignments default to CONFIRMED)
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  Text,
  TextInput,
  Select,
  Textarea,
  Button,
  Group,
  Alert,
  Divider,
  Box
} from '@mantine/core';
import {
  IconUser,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconEdit,
  IconClock,
  IconMapPin,
  IconBulb
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useUpdateAssignment } from '../../hooks/api/useAssignments';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface Doctor {
  id: string;
  name: string;
  email?: string;
  specialty?: string;
  department?: string;
}

interface SessionTime {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

interface Room {
  id: string;
  number: string;
  name: string;
}

interface Assignment {
  id: string;
  title: string;
  description?: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  status: 'CONFIRMED' | 'CANCELLED';
  date: string;
  conflictCode?: string;
  published?: boolean;
  publishedAt?: string;
  room: Room;
  user?: Doctor;
  users?: Doctor;
  doctor?: Doctor;
  sessionTime?: SessionTime;
  session_times?: SessionTime;
}

interface EditAssignmentModalProps {
  opened: boolean;
  onClose: () => void;
  assignment: Assignment | null;
  doctors?: Doctor[];
  sessionTimes?: SessionTime[];
  onSuccess?: () => void;
}

/**
 * EditAssignmentModal Component
 * 
 * Features:
 * - Edit assignment title and description
 * - Reassign doctor
 * - Change session time
 * - Real-time validation
 * - Conflict detection
 * - Role-based access control
 * 
 * Note: Status management removed - assignments are CONFIRMED by default
 */
const EditAssignmentModal: React.FC<EditAssignmentModalProps> = ({
  opened,
  onClose,
  assignment,
  doctors = [],
  sessionTimes = [],
  onSuccess
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [selectedSessionTimeId, setSelectedSessionTimeId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const updateMutation = useUpdateAssignment();

  // Helper to get user data from assignment
  const getUserData = () => {
    if (!assignment) return null;
    return assignment.user || assignment.users || assignment.doctor || null;
  };

  // Helper to get session time data from assignment
  const getSessionTimeData = () => {
    if (!assignment) return null;
    return assignment.sessionTime || assignment.session_times || null;
  };

  // Pre-fill form when assignment changes
  useEffect(() => {
    if (opened && assignment) {
      setTitle(assignment.title || '');
      setDescription(assignment.description || '');
      
      // Fix: Get doctor ID from multiple sources with proper fallback
      const currentUser = getUserData();
      const doctorId = currentUser?.id || assignment.userId || '';
      setSelectedDoctorId(doctorId);
      
      setSelectedSessionTimeId(assignment.sessionTemplateId || '');
      
      // Debug logging for doctor dropdown issue
      console.log('EditAssignmentModal - Assignment data:', {
        assignmentId: assignment.id,
        assignmentUserId: assignment.userId,
        currentUser: currentUser,
        doctorId: doctorId,
        doctorsAvailable: doctors.length,
        doctorOptionsCount: doctorOptions.length,
        doctors: doctors,
        doctorOptions: doctorOptions,
        assignmentDoctor: assignment.doctor,
        assignmentUsers: assignment.users,
        assignmentUser: assignment.user
      });
    }
  }, [opened, assignment, doctors]);

  // Reset form when modal closes
  useEffect(() => {
    if (!opened) {
      setTitle('');
      setDescription('');
      setSelectedDoctorId('');
      setSelectedSessionTimeId('');
    }
  }, [opened]);

  const handleSubmit = async () => {
    if (!assignment) return;

    if (!title.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Assignment title is required',
        color: 'red'
      });
      return;
    }

    if (!selectedDoctorId) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a doctor',
        color: 'red'
      });
      return;
    }

    setSubmitting(true);
    try {
      const updateData: any = {
        title: title.trim(),
        description: description.trim() || undefined,
        userId: selectedDoctorId
      };

      // Only include sessionTemplateId if it has changed and sessionTimes are available
      if (selectedSessionTimeId && sessionTimes.length > 0) {
        updateData.sessionTemplateId = selectedSessionTimeId;
      }

      await updateMutation.mutateAsync({
        id: assignment.id,
        data: updateData
      });

      notifications.show({
        title: 'Assignment Updated',
        message: 'Assignment details have been successfully updated',
        color: 'green',
        icon: <IconCheck size={16} />
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Update Failed',
        message: error instanceof Error ? error.message : 'Failed to update assignment',
        color: 'red',
        icon: <IconX size={16} />
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Prepare doctor options for select with assignment doctor fallback
  const doctorOptions = useMemo(() => {
    const options = [];
    
    // Add doctors from API if available
    if (doctors && doctors.length > 0) {
      options.push(...doctors.map(doctor => ({
        value: doctor.id,
        label: `${doctor.name}${doctor.specialty ? ` - ${doctor.specialty}` : ''}`,
      })));
    }
    
    // If editing and no doctors from API, add assignment's doctor as fallback
    if (assignment && options.length === 0) {
      const currentDoctor = getUserData();
      if (currentDoctor) {
        options.push({
          value: currentDoctor.id,
          label: `${currentDoctor.name}${currentDoctor.specialty ? ` - ${currentDoctor.specialty}` : ''}`,
        });
      }
    }
    
    return options;
  }, [doctors, assignment]);

  // Prepare session time options for select
  const sessionTimeOptions = sessionTimes.map(session => ({
    value: session.id,
    label: `${session.name} (${session.startTime} - ${session.endTime})`,
  }));

  const canSubmit = title.trim() && selectedDoctorId && !submitting && !updateMutation.isPending;

  const currentUser = getUserData();
  const currentSessionTime = getSessionTimeData();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconEdit size={20} />
          <Text fw={600}>Edit Assignment</Text>
        </Group>
      }
      size="lg"
      centered
      closeOnClickOutside={false}
      closeOnEscape={!submitting}
      trapFocus
      returnFocus
    >
      <Stack gap="md">
        {/* Current Assignment Info */}
        {assignment && (
          <Box>
            <Text size="sm" fw={500} mb="xs">Current Assignment</Text>
            <Stack gap="xs">
              <Group gap="sm">
                <IconMapPin size={16} color="var(--mantine-color-blue-6)" />
                <Text size="sm">
                  <strong>Room:</strong> {assignment.room.number} - {assignment.room.name}
                </Text>
              </Group>
              
              <Group gap="sm">
                <IconClock size={16} color="var(--mantine-color-green-6)" />
                <Text size="sm">
                  <strong>Session:</strong> {currentSessionTime?.name} ({currentSessionTime?.startTime} - {currentSessionTime?.endTime})
                </Text>
              </Group>
              
              <Group gap="sm">
                <IconUser size={16} color="var(--mantine-color-purple-6)" />
                <Text size="sm">
                  <strong>Current Doctor:</strong> {currentUser?.name || 'No doctor assigned'}
                </Text>
              </Group>
            </Stack>
          </Box>
        )}

        <Divider />

        {/* Edit Form */}
        <Stack gap="md">
          <TextInput
            label="Assignment Title"
            placeholder="Enter assignment title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            required
            leftSection={<IconEdit size={16} />}
          />

          <Textarea
            label="Description (Optional)"
            placeholder="Add notes or special instructions"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={3}
            maxRows={5}
          />

          <Select
            label="Assign Doctor"
            placeholder="Select a doctor"
            value={selectedDoctorId}
            onChange={(value) => setSelectedDoctorId(value || '')}
            data={doctorOptions}
            searchable
            required
            clearable={false} // Don't allow clearing in edit mode
            disabled={true} // Disable in edit mode
            leftSection={<IconUser size={16} />}
            nothingFoundMessage="No doctors found"
            description="Doctor cannot be changed when editing existing assignment"
          />

          {sessionTimeOptions.length > 0 && (
            <Select
              label="Session Time (Optional)"
              placeholder="Change session time"
              value={selectedSessionTimeId}
              onChange={(value) => setSelectedSessionTimeId(value || '')}
              data={sessionTimeOptions}
              searchable
              leftSection={<IconClock size={16} />}
              description="Leave empty to keep current session time"
            />
          )}
        </Stack>

        {/* Warnings and Helpful Messages */}
        <Stack gap="xs">
          {/* Warning for past assignments */}
          {assignment && new Date(assignment.date) < new Date() && (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm" fw={500}>Past Assignment Warning</Text>
              <Text size="xs" mt={4}>
                This is a past assignment. Editing historical assignments should be done with caution as it affects audit records.
              </Text>
            </Alert>
          )}
          
          {/* Conflict warning */}
          {assignment?.conflictCode && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm" fw={500}>Assignment Has Conflicts</Text>
              <Text size="xs" mt={4}>
                This assignment has unresolved conflicts ({assignment.conflictCode}). Resolve conflicts before publishing.
              </Text>
            </Alert>
          )}
          
          {/* Helpful tip for coordinators */}
          {!assignment?.conflictCode && !assignment?.published && (
            <Alert color="blue" icon={<IconBulb size={16} />}>
              <Text size="sm" fw={500}>Ready to Publish</Text>
              <Text size="xs" mt={4}>
                This assignment is conflict-free. You can publish it after saving to make it visible to doctors.
              </Text>
            </Alert>
          )}
        </Stack>

        {/* Action Buttons */}
        <Group justify="space-between" mt="md">
          <Text size="xs" c="dimmed">
            Tip: Changes are tracked in audit log
          </Text>
          <Group gap="sm">
            <Button
              variant="subtle"
              onClick={onClose}
              disabled={submitting || updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={submitting || updateMutation.isPending}
              leftSection={<IconCheck size={16} />}
            >
              Save Changes
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EditAssignmentModal;

